import { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useLang } from 'src/app/i18n';
import { NOT_FOUND_ERROR_PAGE_PATH } from 'src/app/common/constants';
import { FormMode } from 'src/app/common/types';
import { StepStatusEnum } from 'src/app/common/components/pru-stepped-form-agent';
import {
  EventDetailPageTabEnum,
  EventPublishStatusEnum,
  RegistrationMethodEnum,
  EventCategoryItem,
  EventItem,
  EventTagItem,
} from '../../../types';
import { fetchEventCategoryItem, fetchEventItem, fetchEventTagList } from '../../../network';
import { eventListPath } from '../event-list-routes';
import { isNowAtLeastOneDayBefore, validateRegAllowWalkIn } from './common';
import { ComponentProps } from './event-detail-page.screen';

export enum ComponentEnum {
  BASICS = 'basics',
  DETAILS = 'details',
  REGISTRATION_ATTENDANCE = 'registration_attendance',
  REGISTRATION_FORM = 'registration_form',
  WALK_IN_FORM = 'walk_in_form',
  PUBLISH_SETTING = 'publish_setting',
}

export const useEventDetailPage = ({ cacheParams }: ComponentProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const locale = useLang();
  const { id, categoryId } = useParams<{ id?: string; categoryId?: string }>();
  const { formMode } = useMemo(() => {
    let formMode: FormMode = FormMode.CREATE;
    if (id) {
      if (location.pathname.includes('/view')) {
        formMode = FormMode.VIEW;
      } else if (location.pathname.includes('/edit')) {
        formMode = FormMode.EDIT;
      }
    }
    return { formMode };
  }, [id, location.pathname]);

  const [initialFormMode, setInitialFormMode] = useState<FormMode>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [changesUnsaved, setChangesUnsaved] = useState<boolean>(false);
  const [isReset, setIsReset] = useState<boolean>(false);
  const [tab, setTab] = useState<EventDetailPageTabEnum>(
    cacheParams?.current?.selectedTab || EventDetailPageTabEnum.APPLICATION,
  );
  const [eventCategoryItem, setEventCategoryItem] = useState<EventCategoryItem>();
  const [eventTagList, setEventTagList] = useState<EventTagItem[]>([]);
  const [eventItem, setEventItem] = useState<EventItem>();

  const showLayoutSplashScreen =
    !initialFormMode ||
    (formMode === FormMode.CREATE && !eventCategoryItem) ||
    (formMode === initialFormMode && formMode !== FormMode.CREATE && !eventItem);

  const isPublished = eventItem?.publishStatus === EventPublishStatusEnum.PUBLISHED;
  // for fields that are editable after published and before event date
  const disableEdit = useMemo(() => {
    if (eventItem) {
      if (isPublished) {
        return !isNowAtLeastOneDayBefore(eventItem.eventDate);
      } else if (eventItem.publishStatus === EventPublishStatusEnum.UNPUBLISHED) {
        return true;
      }
    }
    return false;
  }, [eventItem?.publishStatus, eventItem?.eventDate]);

  const walkInAllowed = useMemo(() => {
    if (eventItem && eventItem.regMethod === RegistrationMethodEnum.TEMPLATE) {
      return (
        eventItem.regAllowWalkIn ||
        eventItem.sessions.some(
          (session) => session.regAllowWalkIn || session.subSessions.some((subSession) => subSession.regAllowWalkIn),
        )
      );
    }
    return false;
  }, [eventItem?.regMethod, eventItem?.regAllowWalkIn, eventItem?.sessions]);

  const reloadEventCategoryItem = async () => {
    // categoryId exists only in create mode
    if (categoryId) {
      try {
        const res = await fetchEventCategoryItem(categoryId, dispatch);
        setEventCategoryItem(res);
      } catch (err) {
        history.push(NOT_FOUND_ERROR_PAGE_PATH);
      }
    }
  };

  const reloadEventTagList = async () => {
    try {
      const res = await fetchEventTagList({ locale, pagination: false }, dispatch);
      setEventTagList(res.docs);
    } catch (err) {}
  };

  const reloadData = async (keepLoading?: boolean) => {
    setIsLoading(true);
    await reloadEventCategoryItem();
    await reloadEventTagList();
    if (id) {
      try {
        const res = await fetchEventItem(id, dispatch);
        setEventItem(res);
        setEventCategoryItem(res.category);
      } catch (err) {
        history.push(NOT_FOUND_ERROR_PAGE_PATH);
      }
    }
    if (!keepLoading) {
      setIsLoading(false);
    }
  };

  const getDefaultStatus = (component: ComponentEnum) => {
    if (eventItem) {
      // check the first mandatory field
      switch (component) {
        case ComponentEnum.BASICS:
          return StepStatusEnum.FINISHED;
        case ComponentEnum.DETAILS:
          return eventItem.eventImageType ? StepStatusEnum.FINISHED : undefined;
        case ComponentEnum.REGISTRATION_ATTENDANCE:
          return eventItem.regStartDate && validateRegAllowWalkIn(eventItem) ? StepStatusEnum.FINISHED : undefined;
        case ComponentEnum.REGISTRATION_FORM:
          return eventItem.regFormBody ? StepStatusEnum.FINISHED : undefined;
        case ComponentEnum.WALK_IN_FORM:
          return eventItem.walkInFormBody ? StepStatusEnum.FINISHED : undefined;
        case ComponentEnum.PUBLISH_SETTING:
          return eventItem.pubAgentConfig ? StepStatusEnum.FINISHED : undefined;
        default:
          return undefined;
      }
    }
    return undefined;
  };

  const onBack = () => {
    history.push(eventListPath);
  };

  const handleChangeTab = (_: any, value: EventDetailPageTabEnum) => {
    if (cacheParams?.current && value !== tab) {
      cacheParams.current.selectedTab = value;
    }
    setTab(value);
  };

  useEffect(() => {
    reloadData();
  }, [id]);

  useEffect(() => {
    if (!initialFormMode) {
      setInitialFormMode(formMode);
    }
  }, [formMode, initialFormMode]);

  return {
    formMode,
    isLoading,
    changesUnsaved,
    isReset,
    isPublished,
    disableEdit,
    walkInAllowed,
    tab,
    eventCategoryItem,
    eventTagList,
    eventItem,
    showLayoutSplashScreen,
    setIsLoading,
    setChangesUnsaved,
    setIsReset,
    handleChangeTab,
    reloadData,
    getDefaultStatus,
    onBack,
  };
};
